import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { BaseModalComponent } from 'src/app/core/base-modal.componant';
import { SessionService } from 'src/app/shared/services/session.service';
import { LeagueService } from 'src/app/simulator-booking/services/league.service';
import { LeagueIterationService } from 'src/app/simulator-booking/services/league-iteration.service';
import { provideNativeDateAdapter } from '@angular/material/core';

@Component({
    selector: 'create-league-modal',
    templateUrl: './create-league-modal.component.html',
    styleUrl: './create-league-modal.component.scss',
    providers: [provideNativeDateAdapter()],
    standalone: false
})
export class CreateLeagueModalComponent extends BaseModalComponent implements OnInit {

    public form = new FormGroup({
        name: new FormControl('', {
            validators: Validators.required
        }),
        description: new FormControl(''),
        organizationId: new FormControl('', {
            validators: Validators.required
        }),
        createdBy: new FormControl()
    });

    public iterationForm = new FormGroup({
        year: new FormControl(new Date().getFullYear(), {
            validators: Validators.required
        }),
        startDateOnly: new FormControl('', {
            validators: Validators.required
        }),
        endDateOnly: new FormControl('', {
            validators: Validators.required
        }),
        startTimeOnly: new FormControl('', {
            validators: Validators.required
        }),
        endTimeOnly: new FormControl('', {
            validators: Validators.required
        }),
        entryFee: new FormControl(0, {
            validators: [Validators.required, Validators.min(0)]
        }),
        notes: new FormControl('')
    });

    public createInitialIteration: boolean = true;

    constructor(
        private sessionService: SessionService,
        private leagueService: LeagueService,
        private leagueIterationService: LeagueIterationService,
        public dialogRef: MatDialogRef<CreateLeagueModalComponent>
    ) {
        super();
        this.form.get('organizationId').setValue(this.sessionService.getUser().organizationId);
        this.form.get('createdBy').setValue(this.sessionService.getUserId());
    }

    ngOnInit(): void {
    }

    submit({ value, valid }): void {
        if (valid) {
            this.isLoading = true;
            const formData = { ...value, timeZone: this.sessionService.getUser().timeZone };
            
            // Validate initial iteration dates if createInitialIteration is true
            if (this.createInitialIteration) {
                const startDateOnly = this.iterationForm.get('startDateOnly').value;
                const endDateOnly = this.iterationForm.get('endDateOnly').value;

                // Check required
                if (!startDateOnly || !endDateOnly) {
                    this.iterationForm.get('startDateOnly').setErrors(!startDateOnly ? { required: true } : null);
                    this.iterationForm.get('endDateOnly').setErrors(!endDateOnly ? { required: true } : null);
                    this.isLoading = false;
                    return;
                }

                // Check if dates are valid dates
                const startDate = new Date(startDateOnly);
                const endDate = new Date(endDateOnly);

                if (isNaN(startDate.getTime())) {
                    this.iterationForm.get('startDateOnly').setErrors({ validationErrors: 'Start date is not a valid date' });
                    this.isLoading = false;
                    return;
                }
                if (isNaN(endDate.getTime())) {
                    this.iterationForm.get('endDateOnly').setErrors({ validationErrors: 'End date is not a valid date' });
                    this.isLoading = false;
                    return;
                }

                // Start date should not be after end date
                if (startDate > endDate) {
                    this.iterationForm.get('startDateOnly').setErrors({ validationErrors: 'Start date should not be after end date' });
                    this.iterationForm.get('endDateOnly').setErrors({ validationErrors: 'End date should not be before start date' });
                    this.isLoading = false;
                    return;
                }

                // Validate that start time is before end time
                const startTimeOnly = this.iterationForm.get('startTimeOnly').value;
                const endTimeOnly = this.iterationForm.get('endTimeOnly').value;

                if (startTimeOnly >= endTimeOnly) {
                    this.iterationForm.get('startTimeOnly').setErrors({ validationErrors: 'Start time should not be after end time' });
                    this.iterationForm.get('endTimeOnly').setErrors({ validationErrors: 'End time should not be before start time' });
                    this.isLoading = false;
                    return;
                }
            }

            this.leagueService.create(formData).subscribe({
                next: (result) => {
                    if (result && result.id) {
                        if (this.createInitialIteration && this.iterationForm.valid) {
                            
                            const leagueIterationFormData = {
                                ...this.iterationForm.value,
                                leagueId: result.id,
                                organizationId: this.sessionService.getUser().organizationId,
                                createdBy: this.sessionService.getUserId(),
                                timeZone: this.sessionService.getUser().timeZone
                            };
                            
                            const startDate = new Date(leagueIterationFormData.startDateOnly);
                            const endDate = new Date(leagueIterationFormData.endDateOnly);
                            
                            const startTime = this.iterationForm.get('startTimeOnly').value;
                            const endTime = this.iterationForm.get('endTimeOnly').value;

                            leagueIterationFormData.startDateOnly = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
                            leagueIterationFormData.endDateOnly = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
                            leagueIterationFormData.startTimeOnly = this.formatTimeOnly(startTime);
                            leagueIterationFormData.endTimeOnly = this.formatTimeOnly(endTime);

                            this.leagueIterationService.create(leagueIterationFormData).subscribe({
                                next: () => {
                                    this.dialogRef.close("save");
                                },
                                error: (error) => {
                                    console.error('Error creating initial iteration:', error);
                                    this.setErrorV2(error, this.iterationForm);
                                    this.isLoading = false;
                                },
                                complete: () => this.isLoading = false
                            });
                        } else {
                            this.dialogRef.close("save");
                            this.isLoading = false;
                        }
                    }
                },
                error: (error) => {
                    console.error('Error creating league:', error);
                    this.setErrorV2(error, this.form);
                    this.isLoading = false;
                }
            });
        }
    }

    private formatTimeOnly(value: any): string {
        // Accept Date, time string (HH:mm or HH:mm:ss), or ISO/string parseable by Date
        if (value instanceof Date) {
          const hh = String(value.getHours()).padStart(2, '0');
          const mm = String(value.getMinutes()).padStart(2, '0');
          const ss = String(value.getSeconds()).padStart(2, '0');
          return `${hh}:${mm}:${ss}`;
        }
    
        if (typeof value === 'string') {
          const timeMatch = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
          if (timeMatch) {
            const hh = String(Math.min(23, parseInt(timeMatch[1], 10))).padStart(2, '0');
            const mm = String(Math.min(59, parseInt(timeMatch[2], 10))).padStart(2, '0');
            const ss = String(Math.min(59, timeMatch[3] ? parseInt(timeMatch[3], 10) : 0)).padStart(2, '0');
            return `${hh}:${mm}:${ss}`;
          }
          const asDate = new Date(value);
          if (!isNaN(asDate.getTime())) {
            const hh = String(asDate.getHours()).padStart(2, '0');
            const mm = String(asDate.getMinutes()).padStart(2, '0');
            const ss = String(asDate.getSeconds()).padStart(2, '0');
            return `${hh}:${mm}:${ss}`;
          }
        }
        
        // Unknown structure (e.g., null), default to 00:00:00
        return '00:00:00';
      }
}

